import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Users, Wallet, Plus, Minus, RefreshCw, Search, X,
  ShoppingBag, Box, CheckCircle2, XCircle, Mail, Calendar,
  TrendingUp, Activity, ChevronRight
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { cn, formatNumber, formatDateTime } from '../lib/utils';
import { Card } from './ui/Card';
import { Input, Label } from './ui/Input';
import Button from './ui/Button';
import { SkeletonRow } from './ui/Skeleton';
import EmptyState from './ui/EmptyState';
import StatusBadge from './ui/StatusBadge';
import { MARKETPLACE_META } from './marketplaces';

// ─── Helpers ─────────────────────────────────────────────────────────

function daysBetween(a, b) {
  const ms = new Date(b) - new Date(a);
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function formatRelative(iso) {
  if (!iso) return '—';
  const days = daysBetween(iso, new Date());
  if (days === 0) return 'dziś';
  if (days === 1) return 'wczoraj';
  if (days < 7) return `${days} dni temu`;
  if (days < 30) return `${Math.floor(days / 7)} tyg. temu`;
  if (days < 365) return `${Math.floor(days / 30)} mies. temu`;
  return `${Math.floor(days / 365)} lat temu`;
}

// ─── Daily credits sparkline (SVG inline) ────────────────────────────

function CreditsSparkline({ orders, days = 30 }) {
  const buckets = useMemo(() => {
    const now = new Date();
    const out = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      out.push({ date: d, credits: 0 });
    }
    orders.forEach(o => {
      const oDate = new Date(o.created_at);
      oDate.setHours(0, 0, 0, 0);
      const bucket = out.find(b => b.date.getTime() === oDate.getTime());
      if (bucket) bucket.credits += (o.credits_used || 0);
    });
    return out;
  }, [orders, days]);

  const max = Math.max(...buckets.map(b => b.credits), 1);
  const W = 520, H = 110, P = 6;
  const innerW = W - 2 * P, innerH = H - 2 * P;
  const step = innerW / Math.max(1, buckets.length - 1);
  const points = buckets.map((b, i) => {
    const x = P + i * step;
    const y = P + innerH - (b.credits / max) * innerH;
    return [x, y];
  });
  const pathLine = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const pathArea = pathLine + ` L ${points[points.length - 1][0].toFixed(1)} ${H - P} L ${P} ${H - P} Z`;

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        <defs>
          <linearGradient id="credSparkGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B6CF6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#3B6CF6" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={pathArea} fill="url(#credSparkGrad)" />
        <path d={pathLine} fill="none" stroke="#3B6CF6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => buckets[i].credits > 0 && (
          <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill="#3B6CF6" />
        ))}
      </svg>
      <div className="flex justify-between text-2xs text-ink-400 dark:text-ink-500 mt-1 px-1">
        <span>{buckets[0].date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}</span>
        <span>dziś</span>
      </div>
    </div>
  );
}

// ─── Marketplace breakdown bars ──────────────────────────────────────

function MarketplaceBreakdown({ orders }) {
  const counts = useMemo(() => {
    const map = {};
    orders.forEach(o => {
      if (!o.marketplace) return;
      map[o.marketplace] = (map[o.marketplace] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [orders]);

  if (counts.length === 0) {
    return <div className="text-sm text-ink-500 dark:text-ink-400">Brak zleceń.</div>;
  }

  const max = counts[0][1];

  return (
    <div className="space-y-2">
      {counts.map(([mp, n]) => {
        const meta = MARKETPLACE_META[mp];
        const pct = (n / max) * 100;
        return (
          <div key={mp} className="flex items-center gap-3">
            <div className="flex items-center gap-2 w-28 flex-shrink-0">
              <span className="w-2 h-2 rounded-full" style={{ background: meta?.color || '#A1A1AA' }} />
              <span className="text-xs font-semibold text-ink-700 dark:text-ink-300">{mp}</span>
            </div>
            <div className="flex-1 h-2 bg-ink-100 dark:bg-ink-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, background: meta?.color || '#3B6CF6' }}
              />
            </div>
            <span className="text-xs font-mono font-bold text-ink-900 dark:text-ink-100 w-8 text-right">{n}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── KPI Card ────────────────────────────────────────────────────────

function KPI({ icon: Icon, label, value, sub, tone = 'default' }) {
  const toneClasses = {
    default: 'text-ink-900 dark:text-ink-100',
    success: 'text-emerald-600 dark:text-emerald-400',
    danger: 'text-rose-600 dark:text-rose-400',
    brand: 'text-brand-600 dark:text-brand-400',
  };
  return (
    <div className="p-4 rounded-xl bg-ink-50/60 dark:bg-ink-800/40 border border-ink-100 dark:border-ink-800">
      <div className="flex items-center gap-2 mb-2 text-ink-500 dark:text-ink-400">
        <Icon className="w-4 h-4" />
        <span className="text-2xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className={cn('text-2xl font-bold', toneClasses[tone])}>{value}</div>
      {sub && <div className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Drill-down panel (modal) ────────────────────────────────────────

function ClientDetailModal({ client, allOrders, onClose, onCreditsUpdated }) {
  const [creditDelta, setCreditDelta] = useState(50);
  const [updating, setUpdating] = useState(false);

  const userOrders = useMemo(
    () => allOrders.filter(o => o.user_id === client.id).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [allOrders, client.id]
  );

  const kpi = useMemo(() => {
    const total = userOrders.length;
    const done = userOrders.filter(o => o.status === 'done').length;
    const failed = userOrders.filter(o => o.status === 'failed').length;
    const products = userOrders.reduce((s, o) => s + (o.product_count || 0), 0);
    const credits = userOrders.reduce((s, o) => s + (o.credits_used || 0), 0);
    const successRate = total > 0 ? (done / total * 100).toFixed(0) : 0;
    return { total, done, failed, products, credits, successRate };
  }, [userOrders]);

  const adjustCredits = async (delta) => {
    if (updating) return;
    setUpdating(true);
    try {
      const newBal = Math.max(0, (client.balance || 0) + delta);
      const { error } = await supabase.from('credits').update({ balance: newBal }).eq('user_id', client.id);
      if (error) throw error;
      toast.success(`Saldo zaktualizowane: ${formatNumber(newBal)} (${delta >= 0 ? '+' : ''}${delta})`);
      onCreditsUpdated(client.id, newBal);
    } catch (err) {
      toast.error(`Błąd: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-4xl bg-white dark:bg-ink-900 rounded-2xl shadow-2xl my-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-ink-100 dark:border-ink-800">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-12 h-12 rounded-full bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-brand-600 dark:text-brand-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-ink-900 dark:text-ink-100 truncate">
                {client.full_name || client.email}
              </h2>
              <div className="flex items-center gap-2 mt-1 text-sm text-ink-500 dark:text-ink-400">
                <Mail className="w-3.5 h-3.5" />
                <span className="truncate">{client.email}</span>
                {client.is_admin && (
                  <span className="px-1.5 py-0.5 rounded text-2xs font-bold bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                    ADMIN
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors flex-shrink-0">
            <X className="w-5 h-5 text-ink-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Saldo + inline edit */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-5 rounded-xl bg-gradient-to-br from-brand-50 to-brand-100/40 dark:from-brand-900/30 dark:to-brand-900/10 border border-brand-200 dark:border-brand-800">
            <div>
              <div className="flex items-center gap-2 text-brand-700 dark:text-brand-300 mb-1">
                <Wallet className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-wide">Aktualne saldo</span>
              </div>
              <div className="text-4xl font-bold text-brand-900 dark:text-brand-100">
                {formatNumber(client.balance || 0)}
              </div>
              <div className="text-xs text-brand-700/70 dark:text-brand-300/70 mt-0.5">kredytów dostępnych</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                type="number"
                min="1"
                value={creditDelta}
                onChange={e => setCreditDelta(parseInt(e.target.value) || 0)}
                className="w-24"
              />
              <Button
                variant="success"
                size="md"
                onClick={() => adjustCredits(creditDelta)}
                loading={updating}
                leftIcon={!updating && <Plus className="w-4 h-4" />}
              >
                Dodaj
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={() => adjustCredits(-creditDelta)}
                loading={updating}
                leftIcon={!updating && <Minus className="w-4 h-4" />}
              >
                Odejmij
              </Button>
            </div>
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI icon={ShoppingBag} label="Zlecenia" value={formatNumber(kpi.total)} sub={`${kpi.done} done · ${kpi.failed} fail`} />
            <KPI icon={Box} label="Produkty" value={formatNumber(kpi.products)} sub="łącznie wygenerowano" />
            <KPI icon={Wallet} label="Kredyty zużyte" value={formatNumber(kpi.credits)} sub="historycznie" />
            <KPI icon={CheckCircle2} label="Success rate" value={`${kpi.successRate}%`} sub="zlecenia zakończone" tone={kpi.successRate >= 90 ? 'success' : kpi.successRate >= 70 ? 'default' : 'danger'} />
          </div>

          {/* Wykres zużycia kredytów */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-ink-700 dark:text-ink-300">
              <TrendingUp className="w-4 h-4" />
              <h3 className="text-sm font-semibold">Zużycie kredytów (30 dni)</h3>
            </div>
            <Card className="p-4">
              <CreditsSparkline orders={userOrders} days={30} />
            </Card>
          </div>

          {/* Marketplace breakdown */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-ink-700 dark:text-ink-300">
              <Activity className="w-4 h-4" />
              <h3 className="text-sm font-semibold">Marketplace breakdown</h3>
            </div>
            <Card className="p-4">
              <MarketplaceBreakdown orders={userOrders} />
            </Card>
          </div>

          {/* Timeline ostatnich orderów */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-ink-700 dark:text-ink-300">
              <Calendar className="w-4 h-4" />
              <h3 className="text-sm font-semibold">Ostatnie zlecenia ({Math.min(userOrders.length, 10)})</h3>
            </div>
            {userOrders.length === 0 ? (
              <Card className="p-6">
                <EmptyState icon={<ShoppingBag className="w-6 h-6" />} title="Brak zleceń" description="Ten użytkownik jeszcze nie uruchomił żadnego zlecenia." />
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <div className="divide-y divide-ink-100 dark:divide-ink-800">
                  {userOrders.slice(0, 10).map(o => {
                    const meta = MARKETPLACE_META[o.marketplace];
                    return (
                      <div key={o.id} className="flex items-center gap-3 px-4 py-3 hover:bg-ink-50/60 dark:hover:bg-ink-800/40">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta?.color || '#A1A1AA' }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm font-semibold text-ink-900 dark:text-ink-100">
                            <span>{o.marketplace}</span>
                            {o.language && <span className="text-ink-400 dark:text-ink-500 font-normal">· {o.language}</span>}
                          </div>
                          <div className="text-xs text-ink-500 dark:text-ink-400 font-mono">{formatDateTime(o.created_at)}</div>
                        </div>
                        <div className="text-right hidden sm:block">
                          <div className="text-sm font-bold text-ink-900 dark:text-ink-100">{formatNumber(o.product_count || 0)}</div>
                          <div className="text-2xs text-ink-500 dark:text-ink-400">produktów</div>
                        </div>
                        <div className="text-right hidden md:block">
                          <div className="text-sm font-mono text-ink-700 dark:text-ink-300">{formatNumber(o.credits_used || 0)}</div>
                          <div className="text-2xs text-ink-500 dark:text-ink-400">kredytów</div>
                        </div>
                        <StatusBadge status={o.status} size="sm" />
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main ClientsTab component ───────────────────────────────────────

export default function AdminClientsTab() {
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [profilesRes, ordersRes] = await Promise.all([
        supabase.from('profiles').select('id, email, full_name, is_admin, created_at, balance').order('created_at', { ascending: false }),
        supabase.from('orders').select('id, user_id, email, marketplace, language, product_count, credits_used, status, created_at'),
      ]);
      if (profilesRes.error) throw profilesRes.error;
      if (ordersRes.error) throw ordersRes.error;

      const allOrders = ordersRes.data || [];

      const merged = (profilesRes.data || []).map(p => {
        const userOrders = allOrders.filter(o => o.user_id === p.id);
        const lastOrder = userOrders.reduce((a, b) => (new Date(a?.created_at || 0) > new Date(b.created_at) ? a : b), null);
        return {
          ...p,
          balance: p.balance ?? 0,
          total_orders: userOrders.length,
          total_credits_used: userOrders.reduce((s, o) => s + (o.credits_used || 0), 0),
          total_products: userOrders.reduce((s, o) => s + (o.product_count || 0), 0),
          last_activity: lastOrder?.created_at || null,
        };
      });

      setClients(merged);
      setOrders(allOrders);
    } catch (err) {
      toast.error(`Błąd pobierania klientów: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreditsUpdated = (userId, newBalance) => {
    setClients(prev => prev.map(c => c.id === userId ? { ...c, balance: newBalance } : c));
    if (selected?.id === userId) setSelected(s => ({ ...s, balance: newBalance }));
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return clients;
    const q = search.toLowerCase();
    return clients.filter(c =>
      (c.email || '').toLowerCase().includes(q) ||
      (c.full_name || '').toLowerCase().includes(q)
    );
  }, [clients, search]);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="w-full sm:w-80">
          <Input
            type="search"
            placeholder="Szukaj po e-mail lub imieniu…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <Button
          variant="secondary"
          size="md"
          onClick={fetchAll}
          leftIcon={<RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />}
        >
          Odśwież
        </Button>
      </div>

      {loading ? (
        <Card className="overflow-hidden">
          <div className="divide-y divide-ink-100 dark:divide-ink-800">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} columns={6} />)}
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Users className="w-7 h-7" />}
          title={search ? 'Brak wyników' : 'Brak klientów'}
          description={search ? 'Spróbuj innej frazy.' : 'Żadne konto nie zostało jeszcze utworzone.'}
          action={search && (
            <Button variant="secondary" size="sm" onClick={() => setSearch('')}>
              Wyczyść wyszukiwanie
            </Button>
          )}
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ink-50/80 dark:bg-ink-800/40 border-b border-ink-200 dark:border-ink-800">
                <tr>
                  {['Klient', 'Saldo', 'Zlecenia', 'Produkty', 'Kredyty zużyte', 'Ostatnia akt.', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-2xs font-bold uppercase tracking-wide text-ink-500 dark:text-ink-400 text-left whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
                {filtered.map(c => (
                  <tr
                    key={c.id}
                    className="hover:bg-ink-50/60 dark:hover:bg-ink-800/40 transition-colors cursor-pointer"
                    onClick={() => setSelected(c)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-ink-900 dark:text-ink-100 truncate flex items-center gap-2">
                            {c.full_name || c.email}
                            {c.is_admin && (
                              <span className="px-1 py-0.5 rounded text-2xs font-bold bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">ADMIN</span>
                            )}
                          </div>
                          <div className="text-xs text-ink-500 dark:text-ink-400 truncate">{c.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={cn(
                        'inline-flex items-center gap-1 text-sm font-bold',
                        (c.balance || 0) > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                      )}>
                        <Wallet className="w-3.5 h-3.5" />
                        {formatNumber(c.balance || 0)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-ink-900 dark:text-ink-100 whitespace-nowrap">
                      {formatNumber(c.total_orders)}
                    </td>
                    <td className="px-4 py-3 text-sm text-ink-700 dark:text-ink-300 whitespace-nowrap">
                      {formatNumber(c.total_products)}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-ink-700 dark:text-ink-300 whitespace-nowrap">
                      {formatNumber(c.total_credits_used)}
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-500 dark:text-ink-400 whitespace-nowrap">
                      {formatRelative(c.last_activity)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <ChevronRight className="w-4 h-4 text-ink-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-3 text-xs text-ink-500 dark:text-ink-400 px-1">
          Wyświetlono {formatNumber(filtered.length)} z {formatNumber(clients.length)} klientów
        </div>
      )}

      {selected && (
        <ClientDetailModal
          client={selected}
          allOrders={orders}
          onClose={() => setSelected(null)}
          onCreditsUpdated={handleCreditsUpdated}
        />
      )}
    </div>
  );
}
